import React, { FC, useCallback, useMemo, useState } from 'react';
import ContentBaseLayout from '../../../libx/shared/ContentBaseLayout';
import ProductList from '../../../../shared/common/ProductList';
import { FilterBar } from '../../../../shared/common/FilterBar';
import { LOCAL_COLLECTION_SORT_BY } from '../../../../shared/common/SortByDropDown';
import Pagination from '../../../../shared/common/Pagination';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../../../shared/components/NoElementsFound';
import CollectionListItem from '../../../../shared/common/ListItems/CollectionListItem';
import { ProductGroupTile } from '../ProductGroupTile';
import { globalError } from '../../../../shared/notification/actions';
import Headline from './Headline';
import { CopyDetailsPopup } from '../../../collection/copyDetailsPopup/CopyDetailsPopup';
import { useParams } from 'react-router-dom';
import {
    invalidateLibraryManagedProductGroupFeaturedPages,
    useLibraryManagedProductGroupsFeaturePages
} from '../api/useLibraryManagedProductGroupsFeaturePages';
import { useUpdateProductGroup } from '../../../../api/library-managed/useUpdateProductGroup';
import { useUpdateProductGroupSlot } from '../../../../api/library-managed/useUpdateProductGroupSlot';
import { useDeleteProductFromProductGroup } from '../../../../api/library-managed/useDeleteProductFromProductGroup';
import { useDeleteAllProductsFromProductGroup } from '../../../../api/library-managed/useDeleteAllProductsFromProductGroup';
import { ProductMetaData, ProductSorting } from '../../../../../models/Product';
import {
    invalidateLibraryManagedProductGroupProducts,
    useLibraryManagedProductGroupProducts
} from '../../../../api/library-managed/useLibraryManagedProductGroupProducts';
import { SelectionList } from '../../../../../models/SelectionList';
import { ResponsiveFilterBar } from '../../../../shared/filter/ResponsiveFilterBar';
import { fetchProductGroupDetailsAggregationsQuickSearch } from '../../../../api/library-managed/product-group-detail/fetchProductGroupDetailsAggregationsQuicksearch';
import { QueryKeyBuilder } from '../../../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../../../api/listContent/fetchAggregations';
import { fetchProductGroupDetailsAggregations } from '../../../../api/library-managed/product-group-detail/fetchProductGroupDetailsAggregations';
import { FetchAggregationsQuickSearch } from '../../../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../../../shared/filter/defaultVisibleFilterGroups';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Article, SubscriptionPeriodType } from '../../../../../models/Article';
import { useAddBulkFromProductGroupToSelectionList } from '../../../../api/selection-list/useAddFromProductGroupToList';
import { useAddBulkFromProductGroupToCart } from '../../../../api/shopping-cart/useAddFromProductGroupToCart';
import { useAddToList } from '../../../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../../../api/shopping-cart/useAddToCart';
import { SingleAddOptions } from '../../../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../../../models/ListActionTarget';
import { useListContentChecks } from '../../../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../../../shared/common/CheckBeforeAddingModal';
import { useAddToGroupBulk } from '../../../../api/productGroups/useAddToGroupBulk';
import { useRemoveFromGroupBulk } from '../../../../api/productGroups/useRemoveFromGroupBulk';
import { PriceTier } from '../../../../../models/PriceTier';
import { useProductSelection } from '../../../../shared/product-selection/useProductSelection';
import { hasProductSelection, nothingSelected } from '../../../../shared/product-selection/ProductSelectionUtils';
import { UnavailableProductsModal } from '../../../../shared/common/UnavailableProductsModal';
import { invalidateSelectionLists } from '../../../../api/selection-list/useSelectionLists';
import { useDisplayTypeSearchParam } from '../../../../shared/utils/useDisplayTypeSearchParam';
import { useDisplayFilterSearchParam } from '../../../../shared/utils/useDisplayFilterSearchParam';
import { getLocalisedProductGroupTitle, LANGUAGES } from './mappers';
import { useAuthentication } from '../../../../app-store/authentication/useAuthentication';
import { usePaginationAndSortParams } from '../../../../shared/hooks/usePaginationAndSortParams';
import { useProductFilters } from '../../../../shared/filter/useProductFilters';

export const ProductGroupDetail: FC = () => {
    const { t, i18n } = useTranslation();

    const authentication = useAuthentication();

    const { onSelectButtonClick, productSelection, cancelSelection, selectAll, toggleSelectionMode } =
        useProductSelection();

    const [product, setProduct] = useState<(ProductMetaData & Article) | undefined>(undefined);

    const { productGroupId = '' } = useParams<'productGroupId'>();

    const { data: featuredPages, isLoading: isLoadingFeaturePages } = useLibraryManagedProductGroupsFeaturePages();
    const featuredPage = featuredPages?.find(page =>
        page?.productGroupSlots?.find(slot => slot?.productGroup?.id === parseInt(productGroupId))
    );
    const productGroupSlot = featuredPage?.productGroupSlots?.find(
        slot => slot?.productGroup?.id === parseInt(productGroupId)
    );
    const productGroup = productGroupSlot?.productGroup;

    const productGroupTitle = getLocalisedProductGroupTitle(productGroup?.titles, i18n.language);
    const { displayFilter } = useDisplayFilterSearchParam();
    const {
        page,
        sortBy: sortByParam,
        pageSize
    } = usePaginationAndSortParams({
        defaultPageSize: 54,
        defaultSortBy: ProductSorting.RELEASE_DATE_NEW_TO_OLD
    });

    const { productFilter } = useProductFilters();

    const sortBy =
        sortByParam ?? (productGroup?.sorting === 'custom' ? 'custom' : ProductSorting.RELEASE_DATE_NEW_TO_OLD);
    const { display } = useDisplayTypeSearchParam();

    const { data: products, isLoading: isLoadingProducts } = useLibraryManagedProductGroupProducts({
        productGroupId: productGroupId,
        pagination: { sortBy, page, pageSize },
        filterMap: productFilter,
        enabled: !isLoadingFeaturePages && !!productGroup
    });

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(() => defaultVisibleFilterGroups(authentication), [authentication]);
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'productGroupDetailPage',
            productGroupId,
            'aggregation',
            filters,
            expandedFilterGroups
        ],
        [productGroupId]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchProductGroupDetailsAggregations({ ...options, productGroupId }),
        [productGroupId]
    );
    const fetchAggregationsQuickSeach = useCallback<FetchAggregationsQuickSearch>(
        options => fetchProductGroupDetailsAggregationsQuickSearch({ ...options, productGroupId }),
        [productGroupId]
    );
    //endregion

    const { mutate: editProductGroup, isLoading: isLoadingEditProductGroup } = useUpdateProductGroup();
    const { mutate: toggleActiveState, isLoading: isLoadingToggleProductGroupSlot } = useUpdateProductGroupSlot();
    const { mutateAsync: deleteProductFromProductGroup, isLoading: isLoadingDeleteProductFromProductGroup } =
        useDeleteProductFromProductGroup(productGroupId);
    const { mutate: deleteAllProducts, isLoading: isDeleteProductsLoading } =
        useDeleteAllProductsFromProductGroup(productGroupId);

    async function onRemoveProductFromList(product: ProductMetaData & Article) {
        await deleteProductFromProductGroup(product);
    }

    function handleSaveProductGroupTitle(title: string) {
        if (!productGroup) {
            globalError(t('marketing.productGroups.warnings.saveGroupTitle'));
            return;
        }

        editProductGroup({
            productGroupId: productGroup.id,
            body: { titles: [{ language: LANGUAGES.en, title }], sorting: productGroup.sorting }
        });
    }

    function saveProductGroupActiveState(active) {
        if (!productGroupSlot) {
            globalError(t('marketing.productGroups.warnings.saveGroupActivationState'));
            return;
        }

        toggleActiveState({ slotId: productGroupSlot.id, body: { active } });
    }

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart({
        callback: () => invalidateLibraryManagedProductGroupProducts(productGroupId)
    });

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    const removeFromGroupBulk = useRemoveFromGroupBulk({
        productGroupId,
        onSuccess: () => {
            invalidateLibraryManagedProductGroupFeaturedPages().then();
            invalidateLibraryManagedProductGroupProducts(productGroupId).then();
            cancelSelection();
        },
        productSelection,
        productFilter
    });

    const { isLoading: isLoadingAddToList, addToListBulk: addBulkProductsToList } =
        useAddBulkFromProductGroupToSelectionList({
            productGroupId,
            onSuccess: () => {
                cancelSelection();
            },
            productSelection,
            productFilter
        });

    const { isLoading: isLoadingAddToCart, addToCartBulk: addBulkProductsToCart } = useAddBulkFromProductGroupToCart({
        productGroupId,
        onSuccess: () => {
            invalidateLibraryManagedProductGroupProducts(productGroupId).then();
            cancelSelection();
        },
        productSelection,
        productFilter
    });

    const addToGroupBulk = useAddToGroupBulk({
        sourceProductGroupId: productGroupId,
        productSelection,
        cancelSelection,
        productFilter
    });

    const onBulkAddToList = (
        list: SelectionList,
        performChecks: boolean,
        priceTier: PriceTier,
        subscriptionPeriodType: SubscriptionPeriodType
    ) => {
        return addBulkProductsToList(list.id, performChecks, priceTier, subscriptionPeriodType).then(data => {
            if (!performChecks) {
                invalidateSelectionLists().then();
            }
            return data;
        });
    };

    const excludeSorting = productGroup?.sorting !== 'custom' ? ['custom'] : [];
    const productsFound = (products?.totalHits ?? 0) > 0;

    const totalProducts = products?.totalHits ?? 0;

    const loading =
        isLoadingFeaturePages ||
        isLoadingProducts ||
        isLoadingEditProductGroup ||
        isLoadingToggleProductGroupSlot ||
        isLoadingDeleteProductFromProductGroup ||
        isDeleteProductsLoading ||
        isLoadingAddToList ||
        isLoadingAddToCart;

    return (
        <ContentBaseLayout
            loading={loading}
            documentTitle={t('shop.productGroups.titleDetails')}
            contentComponent={
                <>
                    <Headline
                        title={productGroupTitle}
                        productGroupTitles={productGroup?.titles}
                        isAddToListEnabled={true}
                        active={productGroupSlot?.active ?? false}
                        sorting={productGroup?.sorting!}
                        onSaveTitle={handleSaveProductGroupTitle}
                        onRemoveFromProductGroup={removeFromGroupBulk}
                        onRemoveAllProducts={deleteAllProducts}
                        productSelection={productSelection}
                        isInSelectionMode={hasProductSelection(productSelection)}
                        cancelSelectionMode={cancelSelection}
                        usageStatistics={productGroupSlot?.usageStatistics}
                        onToggleActive={saveProductGroupActiveState}
                        productAmount={products?.totalHits ?? 0}
                        onBulkAddToGroup={addToGroupBulk}
                        onBulkAddToCart={addBulkProductsToCart}
                        onAddToList={onBulkAddToList}
                        isNothingSelected={nothingSelected(productSelection)}
                    />
                    {!productsFound ? null : (
                        <FilterBar
                            totalProducts={totalProducts}
                            sortBy={sortBy}
                            pageSize={pageSize}
                            displayType={display}
                            sorting={LOCAL_COLLECTION_SORT_BY}
                            excludeSorting={excludeSorting}
                            hideGroupBy={true}
                            hideSelectButtons={false}
                            hideSelectAll={false}
                            toggleSelectionMode={toggleSelectionMode}
                            productSelection={productSelection}
                            selectAll={() => selectAll(totalProducts)}
                        />
                    )}
                    <div className="content-area-inner flex-ca">
                        {displayFilter ? (
                            <ResponsiveFilterBar
                                visibleFilterGroups={visibleFilterGroups}
                                buildQueryKey={aggregationsQueryKeyBuilder}
                                fetchAggregations={fetchAggregations}
                                fetchAggregationsQuickSearch={fetchAggregationsQuickSeach}
                            />
                        ) : null}
                        {!productsFound && !loading ? (
                            <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                        ) : (
                            <div className={classNames({ 'min-h-screen': loading }, 'ca-main')}>
                                {!productsFound ? null : (
                                    <div className="ca-main-inner">
                                        <ProductList
                                            displayType={display}
                                            onSelectProduct={onSelectButtonClick}
                                            productSelection={productSelection}
                                            products={products?.hits ?? []}
                                            onAddPrice={changePrice}
                                            isProductSelectable={true}
                                        >
                                            {({ key, isListView, ...value }) =>
                                                isListView ? (
                                                    <CollectionListItem
                                                        {...value}
                                                        key={key}
                                                        showManagePopup={() => setProduct(value.product)}
                                                        onRemoveProductFromList={() =>
                                                            onRemoveProductFromList(value.product)
                                                        }
                                                    />
                                                ) : (
                                                    <ProductGroupTile
                                                        {...value}
                                                        key={key}
                                                        showManagePopup={() => setProduct(value.product)}
                                                        showRemoveButton={true}
                                                        showSelectButton={true}
                                                        onRemoveProductFromList={() =>
                                                            onRemoveProductFromList(value.product)
                                                        }
                                                    />
                                                )
                                            }
                                        </ProductList>
                                        <Pagination totalItems={totalProducts} pageSize={pageSize} />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {product ? (
                        <CopyDetailsPopup
                            onClosePopup={() => setProduct(undefined)}
                            onAddPrice={changePrice}
                            product={product}
                            shopLanguage={undefined}
                        />
                    ) : null}
                    <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
                    <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
                </>
            }
        />
    );
};
